import { useEffect, useState } from "react";
import {
  KB_LIMITS,
  KB_TAGS,
  KB_TAG_LABEL,
  docsFromText,
  nextPackId,
  type KbPack,
  type KbPackDoc,
  type KbTag,
} from "@greg-chick/shared";
import { getAiKb, saveAiKb } from "../lib/contentApi";
import { useRegisterSave } from "../lib/saveBar";
import { Msg, Empty } from "./ui";

/**
 * 📚 **AI 자료** — 도슨트가 근거로 쓸 글을 운영자가 올린다.
 *
 * ## 「학습」이 아니라고 화면에서도 말한다
 * 모델을 학습시키는 게 아니라 **올린 글에서 관련 조각을 찾아 근거로 준다**(RAG).
 * 이 차이를 안 적으면 운영자는 「학습시켰는데 왜 못 알아듣지」로 헤맨다. 그리고 이쪽이
 * 오히려 낫다 — 올리면 즉시 반영되고, **지우면 사라지고**, 어디서 나온 답인지 보인다.
 *
 * ## 조각 수를 보여 준다
 * 붙여넣으면 문단 단위로 잘려 조각이 된다(판정은 shared `chunkText`).
 * 몇 조각이 됐는지 안 보여 주면 「올렸는데 아무 일도 안 일어난 것 같다」가 된다.
 */

const TAG_HINT: Record<KbTag, string> = {
  book: "책 줄거리·등장인물·읽어 주기 요령",
  heritage: "첨성대·석굴암 같은 유적 해설",
  goods: "굿즈 설명·소재·주의사항",
  howto: "이용 방법·자주 묻는 것",
  guardian: "몇 살에 맞는지·교육적으로 어떻게 쓰는지",
};

export function AiKbPanel() {
  const [doc, setDoc] = useState<KbPackDoc>({ packs: [] });
  const [msg, setMsg] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  /** 지금 글을 붙여넣고 있는 묶음 id — 한 번에 하나만 연다(화면이 길어지지 않게). */
  const [openId, setOpenId] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");

  useEffect(() => {
    void getAiKb()
      .then((d) => {
        setDoc(d);
        setLoaded(true);
      })
      .catch((e: Error) => setMsg(`⚠️ 불러오지 못했어요 (${e.message})`));
  }, []);

  async function onSave() {
    const r = await saveAiKb(doc);
    setMsg(`✅ 저장했어요 — 묶음 ${r.packs}개 · 조각 ${r.chunks}개`);
  }

  useRegisterSave({
    id: "aikb",
    label: "AI 자료",
    save: onSave,
    draft: { data: () => doc, restore: (d) => setDoc(d as KbPackDoc) },
  });

  const patch = (id: string, p: Partial<KbPack>) =>
    setDoc((d) => ({ packs: d.packs.map((x) => (x.id === id ? { ...x, ...p } : x)) }));

  const addPack = () => {
    const id = nextPackId(doc);
    setDoc((d) => ({ packs: [...d.packs, { id, name: "", tag: "heritage", docs: [] }] }));
    setOpenId(id);
  };

  /** 묶음 지우기 — **무엇이 사라지는지 먼저 보여준다**(다른 지우기와 같은 방식). */
  const removePack = (p: KbPack) => {
    const ok = window.confirm(
      [
        `'${p.name || p.id}' 묶음을 지울까요?`,
        "",
        p.docs.length ? `자료 조각 ${p.docs.length}개가 사라져요.` : "아직 자료가 없어요.",
        "",
        "이 묶음을 쓰던 친구는 그만큼 아는 게 줄어요.",
        "(이미 승인된 문답은 그대로 남아요.)",
      ].join("\n"),
    );
    if (!ok) return;
    setDoc((d) => ({ packs: d.packs.filter((x) => x.id !== p.id) }));
  };

  /** 붙여넣은 글 → 조각. 제목은 **답의 근거**로 보여 줄 이름이라 비워 두면 안 된다. */
  const addText = (p: KbPack) => {
    const t = text.trim();
    if (!t) return;
    const docs = docsFromText(title, t, `${p.id}_${p.docs.length}`);
    if (docs.length === 0) {
      setMsg("⚠️ 넣을 내용이 없어요.");
      return;
    }
    /**
     * 🔴 **넣은 만큼 들어갔는지 말한다.**
     *
     * 묶음 한 개에 담기는 조각 수에 상한이 있다(`KB_LIMITS.docs`). 예전엔 넘치면
     * **조용히 잘라 놓고** 「20조각으로 나눠 담았어요」라고 했다 — 실제로는 10조각만
     * 들어갔는데. 그 자료로 친구가 답하는 것이라, 빠진 대목은 **영영 모르는 채로 남는다.**
     */
    const merged = [...p.docs, ...docs];
    const kept = merged.slice(0, KB_LIMITS.docs);
    const dropped = merged.length - kept.length;
    patch(p.id, { docs: kept });
    setTitle("");
    setText("");
    setMsg(
      `📚 «${title.trim() || "제목 없음"}» 을(를) ${docs.length - dropped}조각으로 나눠 담았어요.` +
        (dropped
          ? ` ⚠️ ${dropped}조각은 못 담았어요 — 이 묶음은 ${KB_LIMITS.docs}조각까지예요. 새 묶음을 만들어 주세요.`
          : ""),
    );
  };

  if (!loaded && !msg) return <p className="hint">불러오는 중…</p>;

  return (
    <section className="as-sec">
      <h2 className="as-sec__h">📚 AI 자료</h2>
      <p className="console__hint">
        마을 친구가 <b>이 글을 근거로</b> 답해요. 학습시키는 게 아니라 <b>질문에 맞는 대목을 찾아</b>
        보여 주는 방식이라, 올리면 <b>바로 반영되고 지우면 사라져요</b>.
      </p>
      <p className="console__hint">
        여기 올린 글은 <b>아이 화면에 그대로 나가지 않아요.</b> 아이가 보는 건
        [🧸 캐릭터]에서 <b>사장님이 ✅ 승인한 문답</b>뿐이에요.
      </p>
      <Msg text={msg} />

      {doc.packs.length === 0 ? (
        <Empty icon="📚">아직 자료가 없어요. 아래에서 묶음을 하나 만들고 글을 붙여넣어 보세요.</Empty>
      ) : null}

      <div className="kb__list">
        {doc.packs.map((p) => (
          <article key={p.id} className="kb__pack">
            <div className="kb__head">
              <input
                className="kb__name"
                value={p.name}
                onChange={(e) => patch(p.id, { name: e.target.value.slice(0, KB_LIMITS.name) })}
                placeholder="묶음 이름 (예: 경주 유적 해설)"
              />
              <select
                value={p.tag}
                onChange={(e) => patch(p.id, { tag: e.target.value as KbTag })}
                title={TAG_HINT[p.tag]}
              >
                {KB_TAGS.map((t) => (
                  <option key={t} value={t}>
                    {KB_TAG_LABEL[t]}
                  </option>
                ))}
              </select>
              <span className="kb__count">조각 {p.docs.length}</span>
              <button type="button" className="btn btn--sm btn--danger" onClick={() => removePack(p)}>
                🗑️ 지우기
              </button>
            </div>
            <p className="hint kb__taghint">{TAG_HINT[p.tag]}</p>

            {p.docs.length > 0 ? (
              <ul className="kb__docs">
                {p.docs.slice(0, 6).map((d) => (
                  <li key={d.id}>
                    <b>{d.title}</b> <span>{d.text.slice(0, 60)}…</span>
                  </li>
                ))}
                {p.docs.length > 6 ? <li className="hint">외 {p.docs.length - 6}조각</li> : null}
              </ul>
            ) : null}

            {openId === p.id ? (
              <div className="kb__add">
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value.slice(0, KB_LIMITS.title))}
                  placeholder="이 글의 제목 (예: 첨성대) — 답의 근거로 보여 줘요"
                />
                <textarea
                  value={text}
                  onChange={(e) => {
                    // 🔴 잘렸으면 말한다 — 긴 글을 붙여넣고 뒤가 없어진 줄 모르면 안 된다
                    const v = e.target.value;
                    setText(v.slice(0, KB_LIMITS.paste));
                    if (v.length > KB_LIMITS.paste) {
                      setMsg(
                        `⚠️ 붙여넣은 글이 너무 길어요. 앞에서 ${KB_LIMITS.paste.toLocaleString()}자만 남겼어요 — 나머지는 나눠서 담아 주세요.`,
                      );
                    }
                  }}
                  rows={8}
                  placeholder={"여기에 글을 붙여넣으세요.\n\n빈 줄로 나뉜 문단마다 한 조각이 돼요."}
                />
                <div className="kb__addrow">
                  <button type="button" className="btn btn--primary" onClick={() => addText(p)}>
                    ＋ 자료로 담기
                  </button>
                  <button type="button" className="btn" onClick={() => setOpenId(null)}>
                    닫기
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                className="btn btn--sm"
                onClick={() => {
                  setOpenId(p.id);
                  setTitle("");
                  setText("");
                }}
              >
                ✍️ 글 붙여넣기
              </button>
            )}
          </article>
        ))}
      </div>

      {/* 🔴 한도에 닿으면 **회색 단추만** 남아 있었다 — 왜 안 눌리는지 어디에도 없었다.
          이 저장소의 관례는 「단추 대신 이유를 적는다」다(굿즈 [살 수 있는 곳]과 같은 모양). */}
      {doc.packs.length >= KB_LIMITS.packs ? (
        <p className="hint">묶음을 {KB_LIMITS.packs}개까지 만들 수 있어요. 안 쓰는 묶음을 지우면 더 만들 수 있어요.</p>
      ) : (
        <button type="button" className="btn" onClick={addPack}>
          ＋ 새 자료 묶음
        </button>
      )}
    </section>
  );
}
